// The vault: Nest's on-device database. Replaces the old FastAPI + MongoDB
// backend. All finance data is one JSON document, encrypted at rest in
// IndexedDB and only ever decrypted in memory while the app is unlocked.
import { idbGet, idbSet, idbClear } from "./idb";
import {
  PIN_ITERATIONS,
  aesKey,
  fromB64,
  keyFromPin,
  keyFromPrf,
  randomBytes,
  seal,
  sealJSON,
  toB64,
  unseal,
  unsealJSON,
} from "./crypto";

export const BACKUP_APP_ID = "nest-finance";
const FREE_ATTEMPTS = 5;

export function emptyState() {
  return {
    version: 2,
    income: { monthly_after_tax: 0, yearly_salary: 0, yearly_after_tax: 0 },
    expenses: [],
    debts: [],
    wealth: [],
    accounts: [],
    prices: { quotes: {}, fetchedAt: null },
  };
}

// Fill in anything missing (older backups, the original server export).
export function normalise(data) {
  const base = emptyState();
  const s = { ...base, ...data };
  s.income = { ...base.income, ...(data.income || {}) };
  s.prices = { ...base.prices, ...(data.prices || {}) };
  for (const k of ["expenses", "debts", "wealth", "accounts"]) {
    if (!Array.isArray(s[k])) s[k] = [];
  }
  if (!Array.isArray(data.accounts)) s.accounts = accountsFromLatestSnapshot(s.wealth);
  s.version = 2;
  return s;
}

// Data from before live prices: start each asset off as an account holding a
// manual £ value, which can then be switched to live holdings.
function accountsFromLatestSnapshot(wealth) {
  const latest = [...wealth].sort((a, b) => a.month.localeCompare(b.month)).pop();
  if (!latest) return [];
  return latest.assets.map((a) => ({
    id: crypto.randomUUID(),
    name: a.name,
    holdings: [
      { id: crypto.randomUUID(), kind: "cash", name: a.name, amount: a.amount },
    ],
  }));
}

// --- in-memory session --------------------------------------------------
let meta = null; // unencrypted: salts + wrapped keys, never the data itself
let dekRaw = null; // the data key, only while unlocked
let dataKey = null;
let state = null;
let writeChain = Promise.resolve();
const listeners = new Set();

const notify = () => listeners.forEach((fn) => fn());
export const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export async function loadMeta() {
  meta = (await idbGet("meta")) || null;
  return meta;
}

export const isSetUp = () => !!meta;
export const isUnlocked = () => !!dataKey;
export const hasFaceId = () => !!meta?.faceId;
export const getState = () => state;

async function openSession(raw) {
  dekRaw = raw;
  dataKey = await aesKey(raw);
  const box = await idbGet("data");
  state = normalise(box ? await unsealJSON(dataKey, box) : {});
  notify();
}

function persist() {
  const snapshot = state;
  const key = dataKey;
  writeChain = writeChain.then(async () => {
    await idbSet("data", await sealJSON(key, snapshot));
  });
  return writeChain;
}

const saveMeta = () => idbSet("meta", meta);

// Apply a change to the data and save it (encrypted).
export async function update(fn) {
  if (!state) throw new Error("LOCKED");
  const draft = structuredClone(state);
  state = fn(draft) || draft;
  notify();
  await persist();
  return state;
}

// --- set-up, PIN --------------------------------------------------------
export async function setup(pin, initialData) {
  const raw = randomBytes(32);
  const salt = randomBytes(16);
  meta = {
    v: 1,
    salt: toB64(salt),
    iterations: PIN_ITERATIONS,
    pinWrap: await seal(await keyFromPin(pin, salt), raw),
    faceId: null,
    failedAttempts: 0,
    lockedUntil: 0,
  };
  dekRaw = raw;
  dataKey = await aesKey(raw);
  state = normalise(initialData || {});
  await persist();
  await saveMeta();
  navigator.storage?.persist?.().catch(() => {});
  notify();
}

export const lockoutRemaining = () =>
  Math.max(0, (meta?.lockedUntil || 0) - Date.now());

export async function unlockWithPin(pin) {
  if (lockoutRemaining() > 0) throw new Error("LOCKED_OUT");
  let raw;
  try {
    const kek = await keyFromPin(pin, fromB64(meta.salt), meta.iterations);
    raw = await unseal(kek, meta.pinWrap);
  } catch {
    meta.failedAttempts = (meta.failedAttempts || 0) + 1;
    const over = meta.failedAttempts - FREE_ATTEMPTS;
    if (over >= 0) {
      meta.lockedUntil = Date.now() + Math.min(30000 * 2 ** over, 3600000);
    }
    await saveMeta();
    throw new Error("WRONG_PIN");
  }
  meta.failedAttempts = 0;
  meta.lockedUntil = 0;
  await saveMeta();
  await openSession(raw);
}

export async function verifyPin(pin) {
  try {
    const kek = await keyFromPin(pin, fromB64(meta.salt), meta.iterations);
    await unseal(kek, meta.pinWrap);
    return true;
  } catch {
    return false;
  }
}

export async function changePin(newPin) {
  const salt = randomBytes(16);
  meta.salt = toB64(salt);
  meta.iterations = PIN_ITERATIONS;
  meta.pinWrap = await seal(await keyFromPin(newPin, salt), dekRaw);
  await saveMeta();
}

export function lock() {
  dekRaw = null;
  dataKey = null;
  state = null;
  notify();
}

export async function eraseEverything() {
  lock();
  await idbClear();
  meta = null;
  notify();
}

// --- Face ID (passkey + PRF) ---------------------------------------------
export async function faceIdAvailable() {
  try {
    return (
      !!window.PublicKeyCredential &&
      (await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable())
    );
  } catch {
    return false;
  }
}

async function prfOutput(credentialId, prfSalt) {
  const assertion = await navigator.credentials.get({
    publicKey: {
      challenge: randomBytes(32),
      allowCredentials: [{ type: "public-key", id: credentialId }],
      userVerification: "required",
      extensions: { prf: { eval: { first: prfSalt } } },
    },
  });
  const out = assertion.getClientExtensionResults()?.prf?.results?.first;
  if (!out) throw new Error("PRF_UNSUPPORTED");
  return out;
}

export async function enableFaceId() {
  const cred = await navigator.credentials.create({
    publicKey: {
      challenge: randomBytes(32),
      rp: { name: "Nest" },
      user: { id: randomBytes(16), name: "Nest", displayName: "Nest finance" },
      pubKeyCredParams: [
        { type: "public-key", alg: -7 },
        { type: "public-key", alg: -257 },
      ],
      authenticatorSelection: {
        authenticatorAttachment: "platform",
        residentKey: "required",
        userVerification: "required",
      },
      extensions: { prf: {} },
    },
  });
  const prfSalt = randomBytes(32);
  const kek = await keyFromPrf(await prfOutput(cred.rawId, prfSalt));
  meta.faceId = {
    credentialId: toB64(cred.rawId),
    prfSalt: toB64(prfSalt),
    wrap: await seal(kek, dekRaw),
  };
  await saveMeta();
  notify();
}

export async function disableFaceId() {
  meta.faceId = null;
  await saveMeta();
  notify();
}

export async function unlockWithFaceId() {
  const { credentialId, prfSalt, wrap } = meta.faceId;
  const kek = await keyFromPrf(await prfOutput(fromB64(credentialId), fromB64(prfSalt)));
  await openSession(await unseal(kek, wrap));
}

// --- backups -------------------------------------------------------------
export const markBackedUp = () =>
  update((s) => {
    s.lastBackupAt = Date.now();
  });

export function exportBackup() {
  return {
    app: BACKUP_APP_ID,
    format: 2,
    exportedAt: new Date().toISOString(),
    data: state,
  };
}

export function parseBackup(json) {
  const data = json?.app === BACKUP_APP_ID ? json.data : null;
  if (!data || !Array.isArray(data.expenses) || !Array.isArray(data.wealth)) {
    throw new Error("NOT_A_BACKUP");
  }
  return normalise(data);
}

export async function restoreBackup(json) {
  const data = parseBackup(json);
  await update(() => data);
}
